import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  CreditCard as CreditCardIcon,
  Edit,
  Delete as DeleteIcon,
  Star,
  StarBorder,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/store';
import { getCreditCards, addCreditCard, updateCreditCard, deleteCreditCard } from '@/store/slices/userSlice';

const CreditCards: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { creditCards, isLoading, error } = useSelector((state: RootState) => state.user);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [formData, setFormData] = useState({
    cardNumber: '',
    cardholderName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    isDefault: false,
  });

  useEffect(() => {
    dispatch(getCreditCards());
  }, [dispatch]);

  const handleOpenDialog = (mode: 'add' | 'edit', card?: any) => {
    setDialogMode(mode);
    if (mode === 'edit' && card) {
      setSelectedCard(card);
      setFormData({
        cardNumber: '',
        cardholderName: card.cardholderName,
        expiryMonth: card.expiryMonth.toString(),
        expiryYear: card.expiryYear.toString(),
        cvv: '',
        isDefault: card.isDefault,
      });
    } else {
      setSelectedCard(null);
      setFormData({
        cardNumber: '',
        cardholderName: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: '',
        isDefault: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      cardNumber: '',
      cardholderName: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
      isDefault: false,
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (dialogMode === 'add') {
        await dispatch(addCreditCard(formData)).unwrap();
      } else if (dialogMode === 'edit' && selectedCard) {
        await dispatch(updateCreditCard({ id: selectedCard.id, ...formData })).unwrap();
      }
      handleCloseDialog();
    } catch (err: any) {
      console.error('Failed to save credit card:', err);
    }
  };

  const handleDelete = async (cardId: number) => {
    if (!window.confirm('Are you sure you want to delete this credit card?')) {
      return;
    }

    try {
      await dispatch(deleteCreditCard(cardId)).unwrap();
    } catch (err: any) {
      console.error('Failed to delete credit card:', err);
    }
  };

  const handleSetDefault = async (cardId: number) => {
    try {
      await dispatch(updateCreditCard({ id: cardId, isDefault: true })).unwrap();
    } catch (err: any) {
      console.error('Failed to set default card:', err);
    }
  };

  const getCardType = (cardNumber: string) => {
    if (cardNumber.startsWith('4')) return 'Visa';
    if (cardNumber.startsWith('5')) return 'MasterCard';
    if (cardNumber.startsWith('3')) return 'American Express';
    return 'Unknown';
  };

  const formatCardNumber = (cardNumber: string) => {
    if (!cardNumber || cardNumber.length < 4) return '****';
    return `**** **** **** ${cardNumber.slice(-4)}`;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            My Credit Cards
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('add')}
          >
            Add New Card
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch({ type: 'user/clearError' })}>
            {error}
          </Alert>
        )}

        {isLoading && creditCards.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : creditCards.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body1" color="text.secondary">
              No credit cards added yet. Add your first card to get started.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {creditCards.map((card) => (
              <Card key={card.id}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CreditCardIcon color="primary" />
                      <Box>
                        <Typography variant="h6">
                          {card.cardType}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatCardNumber(card.cardLastFour)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {card.isDefault && (
                        <Chip
                          icon={<Star />}
                          label="Default"
                          size="small"
                          color="primary"
                        />
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog('edit', card)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(card.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  </CardContent>
                </Card>
            ))}
          </Box>
        )}
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add New Credit Card' : 'Edit Credit Card'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {dialogMode === 'add' && (
              <TextField
                fullWidth
                label="Card Number"
                name="cardNumber"
                value={formData.cardNumber}
                onChange={handleChange}
                inputProps={{ maxLength: 19 }}
                required
                disabled={isLoading}
                helperText="Enter your 16-digit card number"
              />
            )}

            <TextField
              fullWidth
              label="Cardholder Name"
              name="cardholderName"
              value={formData.cardholderName}
              onChange={handleChange}
              required
              disabled={isLoading}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Expiry Month"
                name="expiryMonth"
                value={formData.expiryMonth}
                onChange={handleChange}
                inputProps={{ min: 1, max: 12 }}
                required
                disabled={isLoading}
                placeholder="MM"
              />
              <TextField
                fullWidth
                label="Expiry Year"
                name="expiryYear"
                value={formData.expiryYear}
                onChange={handleChange}
                inputProps={{ min: new Date().getFullYear(), max: new Date().getFullYear() + 20 }}
                required
                disabled={isLoading}
                placeholder="YYYY"
              />
            </Box>

            <TextField
              fullWidth
              label="CVV"
              name="cvv"
              value={formData.cvv}
              onChange={handleChange}
              inputProps={{ maxLength: 4, minLength: 3 }}
              required
              disabled={isLoading}
              helperText="3 or 4 digits on back of card"
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={20} /> : 'Save Card'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCloseDialog}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          {!selectedCard?.isDefault && (
            <Button
              onClick={() => handleSetDefault(selectedCard?.id)}
              disabled={isLoading}
              startIcon={<StarBorder />}
            >
              Set as Default
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CreditCards;
